<template>
  <div class="article-selector">
    <!-- Header avec navigation et actions -->
    <div class="header-section">
      <div class="header-section_title">
        <h1>
          <button class="btn-back" @click="goBack">
            <span class="icon">←</span>
            {{ $t("collections_and_merch.page.article_selector.title") }}
            {{ collectionTitle || "votre collection" }}
          </button>
        </h1>

        <FillTypeCell
          v-if="showFillTypeCell"
          :fillType="collectionFillType"
          :showIcon="true"
          :tooltipText="currentFillTypeTooltip"
          tooltipPosition="bottom"
        />
      </div>

      <!-- Save Button -->
      <button
        class="btn btn-primary"
        @click="validateSelection"
        :disabled="!isValidationEnabled"
      >
        {{ $t("collections_and_merch.page.article_selector.save_button") }}
        <span v-if="persistentSelection.length > 0">
          {{ $t("collections_and_merch.page.article_selector.mode_manual") }}
        </span>
        <span v-else-if="activeFiltersCount > 0">
          {{ $t("collections_and_merch.page.article_selector.mode_auto") }}
        </span>
      </button>
    </div>

    <!-- Table des articles -->
    <BaseTable
      ref="baseTable"
      :loading="loading || isLoadingFilters"
      :items="articles"
      :columns="tableColumns"
      :selectable="true"
      :itemKey="'id'"
      :showSearch="true"
      :search-fields="[
        'sku',
        'reference',
        'title',
        'type',
        'couleur',
        'univers',
        'prix',
      ]"
      :local-search="false"
      :showPagination="true"
      :currentPage="paginationData.currentPage"
      :totalPages="paginationData.totalPages"
      :totalItems="paginationData.totalItems"
      :itemsPerPage="paginationData.itemsPerPage"
      :tabs="[]"
      :table-filters="baseTableFilters"
      :showFixedActionBar="false"
      :initial-selected-items="currentPageSelection"
      :active-filter-tags="filterTags"
      :showTagLabel="false"
      :search-placeholder="$t('article_put_online.search_placeholder')"
      @remove-filter-tag="handleRemoveTag"
      @update-filter="handleTagUpdate"
      @selection-change="handleSelectionChange"
      @search="handleSearch"
      @page-change="handlePageChange"
      @items-per-page-change="handleItemsPerPageChange"
    >
      <template #header-text>
        <div class="custom-header">
          <h2>{{ pageHeaderText }}</h2>
          <span class="header-subtitle">
            {{ persistentSelection.length }}
            / {{ paginationData.totalItems }}
            {{
              persistentSelection.length <= 1
                ? $t(
                    "collections_and_merch.page.create.articles_section.count_single"
                  )
                : $t(
                    "collections_and_merch.page.create.articles_section.count_plural"
                  )
            }}
          </span>
        </div>
      </template>

      <!-- Le panneau des articles sélectionnés entre actions-wrapper et table-container -->
      <template #selected-items-panel>
        <transition name="slide-fade">
          <div
            v-if="persistentSelection.length > 0"
            class="selected-items-panel"
          >
            <div class="panel-header" @click="toggleSelectedPanel">
              <div class="panel-title">
                <span>
                  {{ persistentSelection.length }}
                  {{
                    persistentSelection.length <= 1
                      ? $t(
                          "collections_and_merch.page.create.articles_section.count_single"
                        )
                      : $t(
                          "collections_and_merch.page.create.articles_section.count_plural"
                        )
                  }}
                </span>
              </div>
              <div
                class="panel-icon"
                :class="{ 'is-open': isSelectedPanelOpen }"
              >
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M18 15l-6-6-6 6"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                </svg>
              </div>
            </div>
            <transition name="collapse">
              <div v-show="isSelectedPanelOpen" class="panel-content">
                <!-- Utiliser BaseTable pour le tableau des articles sélectionnés -->
                <BaseTable
                  :loading="false"
                  :key="'selection-table-' + selectionTableKey"
                  :items="selectedItemsObjects"
                  :columns="tableColumns"
                  :selectable="true"
                  :itemKey="'id'"
                  :showSearch="false"
                  :search-fields="[
                    'sku',
                    'reference',
                    'title',
                    'type',
                    'couleur',
                    'univers',
                    'prix',
                  ]"
                  :local-search="true"
                  :showPagination="false"
                  :showTabsWrapper="false"
                  :showFixedActionBar="false"
                  :initial-selected-items="persistentSelection"
                  @selection-change="handleSelectedItemsChange"
                >
                  <!-- Réutiliser les mêmes templates de cellules que le tableau principal -->
                  <template #cell-image="{ item }">
                    <div class="product-image">
                      <img
                        :src="getProductImage(item)"
                        alt="Product thumbnail"
                      />
                    </div>
                  </template>

                  <template #cell-univers="{ value }">
                    <UniversCell :univers="value" />
                  </template>

                  <template #cell-prix="{ value }"> {{ formatPrice(value) }} </template>

                  <!-- Ajouter une colonne pour le bouton de visualisation -->
                  <template #cell-actions="{ item }">
                    <button
                      class="view-button"
                      @click="viewProductDetails(item)"
                    >
                      Voir
                    </button>
                  </template>
                </BaseTable>
              </div>
            </transition>
          </div>
        </transition>
      </template>

      <!-- Autres templates inchangés... -->
      <template #cell-image="{ item }">
        <div class="product-image">
          <img :src="getProductImage(item)" alt="Product thumbnail" />
        </div>
      </template>

      <template #cell-univers="{ value }">
        <UniversCell :univers="value" />
      </template>

      <template #cell-prix="{ value }"> {{ formatPrice(value) }} </template>

      <template #filters>
        <FilterSidebarTable
          ref="filterSidebar"
          @filters-change="handleFiltersChange"
          :initial-filters="activeFilters"
          :show-apply-button="false"
        >
          <template #filters>
            <FiltersCollection
              :filterStructure="availableFilters"
              :filterValues="filterValues"
              :initialActiveFilters="activeFilters"
              @filters-change="onFiltersCollectionChange"
              @search="handleSearch"
              @automatic-selection="openAutomaticSelectionDialog"
            />
          </template>

          <template #footer-buttons>
            <div class="custom-footer">
              <button
                @click.prevent="applyFilters"
                class="btn btn-primary btn-with-icon"
              >
                {{
                  $t(
                    "collections_and_merch.page.article_selector.filters.apply_button"
                  )
                }}
              </button>
              <button @click.prevent="resetFilters" class="btn btn-reset">
                {{
                  $t(
                    "collections_and_merch.page.article_selector.filters.reset_button"
                  )
                }}
              </button>
            </div>
          </template>
        </FilterSidebarTable>
      </template>

      <template #sort-table>
        <SortSelect
          :options="sortOptions"
          v-model="sortOption"
          :label="$t('collections_and_merch.page.article_selector.sort.label')"
          @change="handleSort"
        />
      </template>

      <template #cell-actions="{ item }">
        <button class="view-button" @click="viewProductDetails(item)">
          {{
            $t("collections_and_merch.page.article_selector.table.actions.view")
          }}
        </button>
      </template>
    </BaseTable>

    <!-- Modale de construction de collection -->
    <BaseModal
      v-if="showBuildingCollectionModal"
      :title="
        $t('collections_and_merch.page.article_selector.modal.building.title')
      "
      :showActions="false"
      :closable="false"
      :showCancel="false"
      :showSubmit="false"
      submitText=""
      cancelText=""
      class="building-collection-modal"
    >
      <template #content>
        <div class="building-collection-content">
          <div class="building-animation">
            <div class="loading-spinner">
              <div class="spinner-circle"></div>
              <div class="spinner-circle spinner-circle-2"></div>
              <div class="spinner-circle spinner-circle-3"></div>
            </div>
          </div>
          <h3>
            {{
              $t(
                "collections_and_merch.page.article_selector.modal.building.message"
              )
            }}
          </h3>
          <p class="building-subtitle">
            {{ buildingMessage }}
          </p>
        </div>
      </template>
    </BaseModal>

    <BaseModal
      v-if="showCancelSelectionModal"
      :title="
        $t(
          'collections_and_merch.page.article_selector.modal.cancel_selection.title'
        )
      "
      :subtitle="
        $t(
          'collections_and_merch.page.article_selector.modal.cancel_selection.subtitle'
        )
      "
      :submitText="
        $t(
          'collections_and_merch.page.article_selector.modal.cancel_selection.submit'
        )
      "
      :cancelText="
        $t(
          'collections_and_merch.page.article_selector.modal.cancel_selection.cancel'
        )
      "
      @close="showCancelSelectionModal = false"
      @submit="confirmCancelSelection"
    >
      <template #content>
        <p>
          {{
            $t(
              "collections_and_merch.page.article_selector.modal.cancel_selection.content"
            )
          }}
        </p>
      </template>
    </BaseModal>
  </div>
</template>

<script>
import BaseTable from "@/components/ui/dataTable/BaseTable.vue";
import FilterSidebarTable from "@/components/ui/dataTable/actions-table/FilterSidebarTable.vue";
import SortSelect from "@/components/ui/dataTable/actions-table/SortSelect.vue";
import FillTypeCell from "@/components/ui/dataTable/FillTypeCell.vue";
import FiltersCollection from "@/components/ui/dataTable/filters/FiltersCollection.vue";
import UniversCell from "@/components/ui/dataTable/UniversCell.vue";
import BaseModal from "@/components/ui/modals/BaseModal.vue";
import { debounce } from "lodash";
import { sortArticles } from "@/utils/articleSortingUtils";
import { mapActions, mapGetters } from "vuex";
import currencyRouteMixin from "@/mixins/currencyRouteMixin";
import dateFormatMixin from "@/mixins/dateFormatMixin";

export default {
  name: "ClientCollectionsArticleSelector",
  mixins: [currencyRouteMixin, dateFormatMixin],
  components: {
    BaseTable,
    BaseModal,
    FilterSidebarTable,
    SortSelect,
    FillTypeCell,
    FiltersCollection,
    UniversCell,
  },
  data() {
    return {
      loading: false,
      isLoadingFilters: true, // Loader pour le chargement initial des filtres
      articles: [],
      // selectedItemsIds et persistentSelection sont maintenant gérés par le store
      isSelectedPanelOpen: true,
      searchQuery: "",
      showSortDropdown: false,
      sortOption: "stockDesc",
      initialSort: null, // Stocker le sort initial pour afficher l'option "manual" si nécessaire
      showFilterSidebar: false,
      // activeFilters maintenant géré par le store
      paginationData: {
        currentPage: 1,
        totalPages: 1,
        totalItems: 0,
        itemsPerPage: 25,
      },
      collectionTitle: "",
      collectionFillType: "",
      isClient: false,
      // selectionTableKey maintenant géré par le store
      availableFilters: {}, // Pour stocker les filtres disponibles par champ
      filterOperators: {}, // Pour stocker les opérateurs disponibles par champ
      initialFilterValues: null,
      filterValues: {},
      filterTags: [],
      allAvailableFilters: null, // Pour stocker TOUS les filtres disponibles sans restrictions
      hasCompleteFilters: false, // Flag pour savoir si on a chargé tous les filtres
      cachedSelectedObjects: [],
      lastSelectionLength: 0,
      debouncedSaveToLocalStorage: debounce(
        this.saveSelectionToLocalStorage,
        300
      ),
      lastAppliedFilters: null,
      lastTimestamp: null,
      // showCancelSelectionModal et état initial maintenant gérés par le store
      pendingFilters: null,
      isUpdatingSelection: false, // Flag pour éviter les boucles infinies
      isUpdatingTags: false, // Flag pour éviter les mises à jour concurrentes de tags
      showBuildingCollectionModal: false,
      buildingMessage: "",
      isLoadingArticles: false, // Flag pour éviter les appels multiples à loadArticles
      lastLoadArticlesParams: null, // Pour détecter les appels identiques
    };
  },
  computed: {
    ...mapGetters("merchandising-collections", [
      "getCollectionFormSelectedIds",
      "getCollectionFormSelectedArticlesData",
    ]),
    // Mode preview : création de collection (pas d'édition)
    isPreviewMode() {
      // Mode preview = pas de collectionId en édition
      return !this.$route.params.collectionId && !this.editingCollectionId;
    },
    tableColumns() {
      return [
        { key: "image", label: "", width: "10%" },
        {
          key: "sku",
          label: this.$t(
            "collections_and_merch.page.article_selector.table.headers.sku"
          ),
          sortable: true,
          width: "5%",
        },
        {
          key: "reference",
          label: this.$t(
            "collections_and_merch.page.article_selector.table.headers.reference"
          ),
          sortable: true,
          width: "20%",
        },
        {
          key: "title",
          label: this.$t(
            "collections_and_merch.page.article_selector.table.headers.title"
          ),
          sortable: true,
          width: "20%",
        },
        {
          key: "type",
          label: this.$t(
            "collections_and_merch.page.article_selector.table.headers.type"
          ),
          sortable: true,
          width: "10%",
        },
        {
          key: "couleur",
          label: this.$t(
            "collections_and_merch.page.article_selector.table.headers.couleur"
          ),
          sortable: true,
          width: "10%",
        },
        {
          key: "univers",
          label: this.$t(
            "collections_and_merch.page.article_selector.table.headers.univers"
          ),
          sortable: true,
          width: "10%",
        },
        {
          key: "prix",
          label: this.$t(
            "collections_and_merch.page.article_selector.table.headers.prix"
          ),
          sortable: true,
          width: "10%",
        },
        { key: "actions", label: "", sortable: false, width: "10%" },
      ];
    },
    sortOptions() {
      const options = [
        {
          value: "stockDesc",
          label: this.$t(
            "collections_and_merch.page.article_selector.sort.options.stock_desc"
          ),
        },
        {
          value: "stockAsc",
          label: this.$t(
            "collections_and_merch.page.article_selector.sort.options.stock_asc"
          ),
        },
        {
          value: "priceDesc",
          label: this.$t(
            "collections_and_merch.page.article_selector.sort.options.price_desc"
          ),
        },
        {
          value: "priceAsc",
          label: this.$t(
            "collections_and_merch.page.article_selector.sort.options.price_asc"
          ),
        },
      ];

      // ✅ Ajouter l'option "manual" uniquement si le sort initial était "manual"
      if (this.initialSort === 'manual') {
        options.push({
          value: "manual",
          label: this.$t(
            "collections_and_merch.page.article_selector.sort.options.manual"
          ),
        });
      }

      return options;
    },
    pageHeaderText() {
      return this.$t(
        "collections_and_merch.page.article_selector.table.list_title"
      );
    },
    // Store getters
    persistentSelection: {
      get() {
        return this.$store.getters["articleSelector/currentSelection"];
      },
      set(value) {
        this.$store.dispatch("articleSelector/updateSelection", value);
      },
    },

    selectedItemsIds: {
      get() {
        return this.$store.getters["articleSelector/currentSelection"];
      },
      set(value) {
        this.$store.dispatch("articleSelector/updateSelection", value);
      },
    },

    selectedItemsObjects() {
      return this.$store.state.articleSelector.currentState.articlesData;
    },

    activeFilters: {
      get() {
        return this.$store.getters["articleSelector/currentFilters"];
      },
      set(value) {
        this.$store.dispatch("articleSelector/updateFilters", value);
      },
    },

    hasUnsavedChanges() {
      return this.$store.getters["articleSelector/hasUnsavedChanges"];
    },

    showCancelSelectionModal: {
      get() {
        return this.$store.getters["articleSelector/showCancelModal"];
      },
      set(value) {
        this.$store.commit("articleSelector/SET_CANCEL_MODAL", value);
      },
    },

    selectionTableKey() {
      return this.$store.getters["articleSelector/tableKey"];
    },

    initialPersistentSelection() {
      return this.$store.getters["articleSelector/initialSelection"] || [];
    },

    initialArticlesData() {
      return this.$store.state.articleSelector.initialState.articlesData || [];
    },

    // Route params
    brandId() {
      return this.$route.params.brandId || this.$route.params.id;
    },
    collectionId() {
      return this.$route.params.collectionID;
    },
    currentFillTypeTooltip() {
      const tooltips = {
        auto: this.$t("collections.fillType.auto_tooltip"),
        manual: this.$t("collections.fillType.manual_tooltip"),
        // Support des anciennes valeurs au cas où
        Automatique: this.$t("collections.fillType.auto_tooltip"),
        Manuel: this.$t("collections.fillType.manual_tooltip"),
        Manuelle: this.$t("collections.fillType.manual_tooltip"),
      };

      return (
        tooltips[this.collectionFillType] ||
        this.$t("collections.fillType.default_tooltip")
      );
    },
    currentPageSelection() {
      const currentPageIds = this.articles.map((article) => article.id);
      return this.persistentSelection.filter((id) =>
        currentPageIds.includes(id)
      );
    },

    baseTableFilters() {
      return Object.entries(this.availableFilters).map(([key, cfg]) => {
        const rawValues = this.filterValues[key];
        const options = Array.isArray(rawValues)
          ? rawValues.map((v) => ({ id: v, label: v }))
          : Array.isArray(rawValues?.values)
          ? rawValues.values.map((v) => ({ id: v, label: v }))
          : (cfg.values || []).map((v) => ({ id: v, label: v }));
        return {
          key,
          options,
          operator: cfg.operator || [],
        };
      });
    },

    isValidationEnabled() {
      return this.persistentSelection.length > 0 || this.activeFiltersCount > 0;
    },

    // Déterminer le mode (automatique ou manuel)
    currentMode() {
      return this.persistentSelection.length > 0 ? "manual" : "auto";
    },
    activeFiltersCount() {
      // on ignore toutes les clés qui commencent par "_" (techniques)
      return Object.keys(this.activeFilters).filter(
        (key) => !key.startsWith("_")
      ).length;
    },
    showFillTypeCell() {
      return this.persistentSelection.length > 0 || this.activeFiltersCount > 0;
    },
  },
  async created() {
    // Réinitialiser les valeurs globales de prix pour éviter d'utiliser des valeurs obsolètes
    window.maxPriceValue = undefined;
    window.originalMaxPriceValue = undefined;

    // Initialiser le store pour cette session
    this.$store.dispatch("articleSelector/initializeArticleSelector", {
      collectionId: this.collectionId,
      brandId: this.brandId,
    });

    const mode = localStorage.getItem("collectionMode") || "auto";
    this.collectionFillType = mode === "auto" ? "auto" : "manual";

    // Attendre un court délai pour laisser le temps à la page Details de capturer l'état initial
    await new Promise((resolve) => setTimeout(resolve, 100));

    // Charger les filtres actifs depuis localStorage et les mettre dans le store
    const savedActiveFilters = localStorage.getItem(
      "articleSelectorActiveFilters"
    );
    if (savedActiveFilters && savedActiveFilters !== "undefined") {
      try {
        const parsed = JSON.parse(savedActiveFilters);
        if (Object.keys(parsed).length > 0) {
          this.$store.dispatch("articleSelector/updateFilters", parsed);
          this.filterTags = this.convertFiltersToTags(parsed);
        } else {
          localStorage.removeItem("articleSelectorActiveFilters");
        }
      } catch (e) {
        console.warn("Filtres mal formés :", e);
      }
    }

    // 4. Charger les valeurs initiales des filtres (structure complète)
    const savedInitialFilterValues = localStorage.getItem(
      "initialFilterValues"
    );
    if (savedInitialFilterValues) {
      try {
        this.initialFilterValues = JSON.parse(savedInitialFilterValues);
        this.filterValues = this.initialFilterValues;
      } catch (e) {
        console.warn(
          "Valeurs initiales des filtres mal formées dans localStorage:",
          e
        );
      }
    }

    // 5. Initialiser le cache des articles sélectionnés
    this._selectedItemsCache = {};
    this.pendingFilters = null;

    // 6. Gestion différente selon le mode
    if (mode === "auto") {
      // En mode automatique, ne pas charger d'articles comme "sélectionnés"
      this.persistentSelection = [];
      this.selectedItemsIds = [];

      // Charger les filtres de la collection si on est en mode édition
      const editingFilters = localStorage.getItem("editingCollectionFilters");
      if (editingFilters && this.collectionId) {
        try {
          const parsedFilters = JSON.parse(editingFilters);
          console.log("Filtres d'édition trouvés:", parsedFilters);
        } catch (e) {
          console.warn("Filtres d'édition mal formés:", e);
        }
      }
    } else {
      // Fallback: si le store est vide, charger depuis localStorage
      let initialSelection = [...this.initialPersistentSelection];
      let initialArticlesData = this.initialArticlesData;

      if (initialSelection.length === 0) {

        // Utiliser les getters du store merchandising-collections
        const savedSelection = this.getCollectionFormSelectedIds;
        const savedArticlesData = this.getCollectionFormSelectedArticlesData;

        if (savedSelection && savedSelection.length > 0) {
          initialSelection = [...savedSelection];
        }

        if (savedArticlesData && savedArticlesData.length > 0) {
          initialArticlesData = [...savedArticlesData];
         
          // Mettre à jour le store articleSelector avec les données des articles
          this.$store.commit(
            "articleSelector/SET_CURRENT_ARTICLES_DATA",
            initialArticlesData
          );
        }
      }

      this.persistentSelection = [...initialSelection];
      this.selectedItemsIds = [...initialSelection];

      // S'assurer que le panneau est ouvert si on a des articles sélectionnés
      if (this.persistentSelection.length > 0) {
        this.isSelectedPanelOpen = true;
      }

      // S'assurer que le mode d'affichage est correct pour une collection manuelle
      this.collectionFillType = "manual";
    }

    // 6.5. Charger le sort depuis localStorage
    const savedSort =
      localStorage.getItem("editedCollectionSort") ||
      JSON.parse(localStorage.getItem("collectionCreateState") || "{}")?.sort;
    if (savedSort) {
      this.sortOption = savedSort;
      this.initialSort = savedSort; // Capturer le sort initial
    } else {
      // Valeur par défaut
      this.sortOption = "stockDesc";
      this.initialSort = "stockDesc";
    }

    // 7. Charger les filtres disponibles et les articles
    this.loadAvailableFilters();

   
    // 8. Initialiser la recherche debounce
    this.debounceSearch = debounce((query) => {
      this.searchQuery = query;
      this.paginationData.currentPage = 1;
      this.loadArticles();
    }, 300);

    // 9. Charger les articles déjà sélectionnés depuis l'API si on édite une collection existante
    if (this.collectionId) {
      this.loadSelectedArticles();
    }

    // 10. Debounce les opérations coûteuses
    this.debouncedUpdateSelectedItemsList = debounce(
      this.updateSelectedItemsList,
      150
    );
    this.debouncedSaveToLocalStorage = debounce(
      this.saveSelectionToLocalStorage,
      300
    );
    this.debouncedLoadArticles = debounce(this.loadArticles, 100);
  },

  beforeDestroy() {
    // Nettoyer le store lorsque le composant est détruit
    this.$store.dispatch("articleSelector/cleanupSession");
  },

  methods: {
    // Méthode pour récupérer la locale du client depuis le sessionStorage
    getClientLocale() {
      try {
        const clientsCms = JSON.parse(
          sessionStorage.getItem("clientsCms") || "[]"
        );
        const currentBrandId = this.brandId;

        if (currentBrandId && Array.isArray(clientsCms)) {
          // Convertir currentBrandId en number pour la comparaison car les IDs dans clientsCms sont des numbers
          const brandIdAsNumber = parseInt(currentBrandId, 10);
          const currentClient = clientsCms.find(
            (client) => client.id === brandIdAsNumber
          );

          if (currentClient && currentClient.defaultLocaleCode) {
            return currentClient.defaultLocaleCode;
          }
        }

        // Fallback sur fr_FR si pas trouvé
        console.warn(
          "🌍 Vue - Impossible de récupérer la locale du client, fallback sur fr_FR"
        );
        console.warn("🌍 DEBUG Vue - currentBrandId:", currentBrandId);
        console.warn(
          "🌍 DEBUG Vue - clientsCms:",
          clientsCms.map((c) => ({
            id: c.id,
            name: c.name,
            defaultLocaleCode: c.defaultLocaleCode,
          }))
        );
        return "fr_FR";
      } catch (error) {
        console.error(
          "🌍 Vue - Erreur lors de la récupération de la locale du client:",
          error
        );
        return "fr_FR";
      }
    },

    // Méthode pour formater le prix avec la devise
    formatPrice(price) {
      if (!price && price !== 0) return "";
      
      const currencyCode =
        this.currentCurrency && this.currentCurrency.toLowerCase() === "euro"
          ? "EUR"
          : this.currentCurrency?.toUpperCase() || "EUR";

      const locale = this.$i18n.locale || "fr-FR";
      return new Intl.NumberFormat(locale, {
        style: "currency",
        currency: currencyCode,
        currencyDisplay: "narrowSymbol",
      }).format(price);
    },

    async loadCollectionData() {
      if (!this.collectionId) return;

      try {
        this.loading = true;
        const collection = await this.$store.dispatch(
          "merchandising-collections/fetchMerchandisingCollectionById",
          {
            brandId: this.brandId,
            collectionId: this.collectionId,
          }
        );

        if (collection) {
          this.collectionTitle = collection.title || "la collection";
          this.collectionFillType = collection.fillType || "auto";
        }
      } catch (error) {
        console.error("Erreur lors du chargement de la collection:", error);
        this.$store.commit("SET_NOTIFICATION_MESSAGE", {
          text: `Erreur lors du chargement de la collection: ${
            error.message || "Une erreur est survenue"
          }`,
          color: "error",
          show: true,
        });
      } finally {
        this.loading = false;
      }
    },

    areArraysEqual(array1, array2) {
      if (!Array.isArray(array1) || !Array.isArray(array2)) {
        return false;
      }

      if (array1.length !== array2.length) {
        return false;
      }

      // Trier les deux tableaux pour une comparaison fiable
      const sortedArr1 = [...array1].sort();
      const sortedArr2 = [...array2].sort();

      return sortedArr1.every((val, idx) => val === sortedArr2[idx]);
    },

    // captureInitialState() supprimée - maintenant gérée par le store

    async loadSelectedArticles() {
      // ✅ Vérifier que collectionId est défini ET n'est pas undefined/null
      if (!this.collectionId || this.collectionId === 'undefined' || this.collectionId === 'null') {
        console.warn('⚠️ collectionId est invalide, annulation du chargement des articles');
        return;
      }

      try {
        const articlesResponse = await this.$store.dispatch(
          "merchandising-collections/fetchMerchandisingCollectionArticles",
          {
            brandId: this.brandId,
            collectionId: this.collectionId,
          }
        );

        if (articlesResponse && articlesResponse.items) {
          // Récupérer les IDs des articles sélectionnés
          const selectedIds = articlesResponse.items.map(
            (item) => item.ti_id || item.id
          );

          // Mettre à jour la sélection dans le store
          this.$store.dispatch("articleSelector/updateSelection", selectedIds);

          // Préparer les données complètes des articles
          const articlesData = articlesResponse.items.map((article) => {
            const currentLocale = this.getClientLocale();
            const metadata = article.ti_metadata?.[currentLocale] || article.ti_metadata?.fr_FR || {};
            return {
              id: article.ti_id,
              reference: article.ti_reference,
              sku: article.ti_sku || article.ti_reference || "-",
              title: metadata.title || article.ti_slug || "Sans titre",
              type: metadata.type || "-",
              couleur: article.ti_color || metadata.color || "-",
              univers: metadata.gender || "-",
              prix: article.ti_priceResale / 100 || 0,
              image: this.extractImageUrl(article),
              rawData: article,
            };
          });

          // Capturer l'état initial dans le store
          this.$store.dispatch("articleSelector/captureInitialState", {
            persistentSelection: selectedIds,
            articlesData: articlesData,
            mode: selectedIds.length > 0 ? "manual" : "auto",
            activeFilters: this.activeFilters,
            filters: this.availableFilters,
          });

          // Mettre à jour le type de remplissage
          if (this.persistentSelection.length > 0) {
            this.collectionFillType = "manual";
          }

          // Sauvegarder AUSSI dans localStorage pour persistance
          if (this.collectionId) {
            // Stocker les IDs d'articles sélectionnés dans le store au lieu du localStorage
            this.$store.dispatch(
              "merchandising-collections/updateSelectedIdsByCollection",
              {
                brandId: this.brandId,
                collectionId: this.collectionId,
                ids: selectedIds,
              }
            );
            localStorage.setItem(
              `selectedArticlesData_${this.brandId}_${this.collectionId}`,
              JSON.stringify(this.initialArticlesData)
            );
          }

          this.$store.commit("articleSelector/INCREMENT_TABLE_KEY");
        }
      } catch (error) {
        console.error(
          "Erreur lors du chargement des articles sélectionnés:",
          error
        );
      }
    },

    // Basculer l'affichage du panneau de sélection
    toggleSelectedPanel() {
      this.isSelectedPanelOpen = !this.isSelectedPanelOpen;
    },

    handleSelectedItemsChange(selectedIds) {
      // Éviter la boucle infinie en ignorant les mises à jour pendant une synchronisation
      if (this.isUpdatingSelection) {
        return;
      }

      // Nous traitons uniquement les IDs des articles actuellement affichés
      // dans le panneau de sélection
      const selectedItemsObjectIds = this.selectedItemsObjects.map(
        (item) => item.id
      );

      // Les IDs que l'utilisateur a déselectionné dans le panneau
      const deselectedIds = selectedItemsObjectIds.filter(
        (id) => !selectedIds.includes(id)
      );

      // Retirer les articles désélectionnés de la sélection persistante
      if (deselectedIds.length > 0) {
        this.persistentSelection = this.persistentSelection.filter(
          (id) => !deselectedIds.includes(id)
        );

        // IMPORTANT: Aussi supprimer les données d'articles du store pour mettre à jour le panneau
        const currentArticlesData =
          this.$store.state.articleSelector.currentState.articlesData || [];
        const updatedArticlesData = currentArticlesData.filter(
          (article) => !deselectedIds.includes(article.id)
        );
        this.$store.commit(
          "articleSelector/SET_CURRENT_ARTICLES_DATA",
          updatedArticlesData
        );

        // IMPORTANT: Vérifier si tous les produits ont été désélectionnés
        if (this.persistentSelection.length === 0) {
          this.collectionFillType = "auto";
        } else {
          this.collectionFillType = "manual";
        }

        // Marquer qu'on est en train de mettre à jour pour éviter la boucle
        this.isUpdatingSelection = true;

        // Mettre à jour aussi la sélection dans le tableau principal
        setTimeout(() => {
          if (this.$refs.baseTable) {
            const currentPageIds = this.articles.map((a) => a.id);
            const currentPageSelection = this.persistentSelection.filter((id) =>
              currentPageIds.includes(id)
            );

            this.$refs.baseTable.selectedItems = currentPageSelection;
          }

          // Forcer le re-rendu du tableau de sélection
          this.$store.commit("articleSelector/INCREMENT_TABLE_KEY");

          // Libérer le flag après la mise à jour
          setTimeout(() => {
            this.isUpdatingSelection = false;
          }, 100);
        }, 0);

        // Sauvegarder la sélection modifiée
        this.saveSelectionToLocalStorage();
      }
    },

    // Récupère un article par son ID
    getItemById(id) {
      return this.articles.find((item) => item.id === id);
    },

    // Récupère l'URL de l'image pour un article par son ID
    getImageForItem(id) {
      const item = this.getItemById(id);
      return item ? this.getProductImage(item) : "";
    },

    // Retire un article de la sélection
    removeFromSelection(id) {
      // Éviter la boucle infinie
      if (this.isUpdatingSelection) {
        return;
      }

      // Retirer de la sélection persistante
      this.persistentSelection = this.persistentSelection.filter(
        (itemId) => itemId !== id
      );

      // Mettre à jour les IDs pour le tableau de sélection
      this.selectedItemsIds = this.selectedItemsIds.filter(
        (itemId) => itemId !== id
      );

      // IMPORTANT: Aussi supprimer les données d'articles du store pour mettre à jour le panneau
      const currentArticlesData =
        this.$store.state.articleSelector.currentState.articlesData || [];
      const updatedArticlesData = currentArticlesData.filter(
        (article) => article.id !== id
      );
      this.$store.commit(
        "articleSelector/SET_CURRENT_ARTICLES_DATA",
        updatedArticlesData
      );

      // Marquer qu'on est en train de mettre à jour
      this.isUpdatingSelection = true;

      // Mettre à jour aussi la sélection dans le tableau principal
      setTimeout(() => {
        if (this.$refs.baseTable) {
          this.$refs.baseTable.selectedItems = this.currentPageSelection;
        }

        // Incrémenter la clé pour forcer le re-rendu
        this.$store.commit("articleSelector/INCREMENT_TABLE_KEY");

        // Libérer le flag après la mise à jour
        setTimeout(() => {
          this.isUpdatingSelection = false;
        }, 100);
      }, 0);
    },

    // hasUnsavedChanges() maintenant géré par le computed getter du store

    goBack() {

      if (this.hasUnsavedChanges) {
        this.showCancelSelectionModal = true;
      } else {
        // Pas de modifications, retour direct sans modal
        this.directGoBack();
      }
    },
    directGoBack() {
      // Nettoyer seulement les données temporaires, pas les données de la collection
      this.cleanupTemporaryData();
      this.$router.go(-1);
    },

    cleanupTemporaryData() {
      // Nettoyer seulement les données temporaires de navigation
      localStorage.removeItem("editingCollectionFilters");
      localStorage.removeItem("pendingFilters");
      localStorage.removeItem("initialFilterValues");
    },

    confirmCancelSelection() {

      // 1) Restaurer l'état ORIGINAL de la collection dans le localStorage
      if (this.collectionId) {
       
        // Stocker les IDs d'articles sélectionnés dans le store au lieu du localStorage
        this.$store.dispatch(
          "merchandising-collections/updateSelectedIdsByCollection",
          {
            brandId: this.brandId,
            collectionId: this.collectionId,
            ids: this.initialPersistentSelection,
          }
        );
        localStorage.setItem(
          `selectedArticlesData_${this.brandId}_${this.collectionId}`,
          JSON.stringify(this.initialArticlesData)
        );

        // Supprimer les clés d'édition temporaires
        localStorage.removeItem("editedCollectionMode");
        localStorage.removeItem("editedCollectionArticleIds");
        localStorage.removeItem("editedCollectionArticlesData");
        localStorage.removeItem("collectionWasEdited");
      } else {
        // Pour création de collection, restaurer l'état de création
        localStorage.setItem(
          "selectedCollectionArticles",
          JSON.stringify(this.initialPersistentSelection)
        );
        // Migration: Utiliser uniquement le store Vuex au lieu de localStorage
        this.$store.dispatch(
          "merchandising-collections/updateCollectionFormSelectedArticlesData",
          {
            articlesData: this.initialArticlesData,
            isPreviewMode: this.isPreviewMode
          }
        );
      }

      // 2) Restaurer le mode et les filtres
      localStorage.setItem("collectionMode", this.initialMode);

      if (this.initialMode === "auto") {
        localStorage.setItem(
          "collectionFilters",
          JSON.stringify(this.initialFilters)
        );
      } else {
        localStorage.removeItem("collectionFilters");
      }

      localStorage.setItem(
        "articleSelectorActiveFilters",
        JSON.stringify(this.initialActiveFilters)
      );


      // 3) Fermer le modal et retourner
      this.showCancelSelectionModal = false;
      this.$router.go(-1);
    },

    async validateSelection() {
      this.loading = true;

      // Afficher la modale de construction
      this.showBuildingCollectionModal = true;
      this.buildingMessage =
        this.persistentSelection.length > 0
          ? this.$t(
              "collections_and_merch.page.article_selector.modal.building.message_manual",
              { count: this.persistentSelection.length }
            )
          : this.$t(
              "collections_and_merch.page.article_selector.modal.building.message_auto"
            );

      try {
        // Déterminer le mode en fonction des filtres et des sélections
        let mode;
        if (
          Object.keys(this.activeFilters).length > 0 &&
          this.persistentSelection.length === 0
        ) {
          mode = "auto";
        } else if (this.persistentSelection.length > 0) {
          mode = "manual";
        } else {
          // Si ni filtres ni sélection, utiliser le mode actuel
          mode = localStorage.getItem("collectionMode") || "auto";
        }

        let articleIds = [];
        let filteredArticles = []; // Pour stocker les données complètes des articles

        // Préparer les filtres au nouveau format objet
        let formattedFilters = {};

        if (mode === "auto" && Object.keys(this.activeFilters).length > 0) {
          // Utiliser la méthode buildFilterQueryParams pour générer le bon format
          formattedFilters = this.buildFilterQueryParams();

          // Mettre à jour le localStorage pour le mode automatique
          localStorage.setItem("collectionMode", "auto");

          // Mode automatique: récupérer TOUS les articles correspondant aux filtres
          const allArticlesParams = {
            ...this.buildFilterQueryParams(),
          };

          // Appeler l'API pour récupérer tous les articles correspondant aux filtres
          const response = await this.$store.dispatch(
            "merchandising-collections/fetchFilteredArticlesForCollection",
            {
              brandId: this.brandId,
              params: allArticlesParams,
            }
          );

          if (response && response.items) {
            // Extraire tous les IDs des articles
            articleIds = response.items
              .map((article) => article.ti_id || article.id)
              .filter((id) => !!id);

            // Stocker les données complètes des articles
            filteredArticles = response.items.map((article) => {
              const currentLocale = this.getClientLocale();
            const metadata = article.ti_metadata?.[currentLocale] || article.ti_metadata?.fr_FR || {};
              return {
                id: article.ti_id,
                reference: article.ti_reference,
                sku: article.ti_sku || article.ti_reference || "-",
                title: metadata.title || article.ti_slug || "Sans titre",
                type: metadata.type || "-",
                couleur: article.ti_color || metadata.color || "-",
                univers: metadata.gender || "-",
                prix: article.ti_priceResale / 100 || 0,
                image: this.extractImageUrl(article),
                rawData: article,
              };
            });
          }
        } else if (mode === "manual") {
          // Mode manuel: utiliser uniquement les articles sélectionnés
          articleIds = this.selectedItemsObjects
            .map((article) => article.id || article.ti_id)
            .filter((id) => !!id);

          filteredArticles = this.selectedItemsObjects;

          // **NOUVEAU** : Préparer les filtres même en mode manuel avec le nouveau format
          if (Object.keys(this.activeFilters).length > 0) {
            formattedFilters = this.buildFilterQueryParams();
          }

          // Définir le mode
          localStorage.setItem("collectionMode", "manual");
        }

        // Sauvegarder les IDs dans le store Vuex
        this.updateCollectionFormSelectedArticles(articleIds);

        // Sauvegarder aussi les données complètes
        this.updateCollectionFormSelectedArticlesData(filteredArticles);

        // Utiliser collectionId ou fallback vers editingCollectionId localStorage
        const effectiveCollectionId =
          this.collectionId || localStorage.getItem("editingCollectionId");

        if (effectiveCollectionId) {
          // ========== MODE MODIFICATION : PATCH API IMMÉDIAT ==========
          console.log('🔄 Modification collection existante - PATCH immédiat');

          this.buildingMessage = "Enregistrement en cours...";

          try {
            // Préparer le payload pour l'API v3
            const payload = {
              collectionId: effectiveCollectionId,
              collectionData: {
                brandId: parseInt(this.brandId, 10),
                newIds: articleIds,
                merchActions: [], // Tous les articles sont nouveaux/modifiés (pas de drag & drop)
                mode: mode,
                state: "completed",
                sort: this.sortOption || "manual",
                filters: formattedFilters,
                metadata: {} // Les métadonnées sont gérées sur la page Details
              },
              silent: false // Afficher le loader
            };

            console.log('📤 PATCH API v3 - Article selector validation:', {
              collectionId: effectiveCollectionId,
              articlesCount: articleIds.length,
              mode: mode,
              filters: Object.keys(formattedFilters)
            });

            // PATCH API immédiat
            await this.$store.dispatch(
              "merchandising-collections/updateMerchandisingCollection",
              payload
            );

            // Nettoyer le mode édition du store
            await this.$store.dispatch("merchandising-collections/clearEditingMode");

            // ✅ Nettoyer le localStorage pour éviter que le bouton "Mettre à jour" reste actif
            localStorage.removeItem("collectionWasEdited");
            localStorage.removeItem("editingCollectionId");
            localStorage.removeItem("editedCollectionMode");
            localStorage.removeItem("editedCollectionArticleIds");
            localStorage.removeItem("editedCollectionArticlesData");
            localStorage.removeItem("editedCollectionFilters");
            localStorage.removeItem("editedCollectionActiveFilters");
            localStorage.removeItem("editedCollectionSort");

            console.log('✅ Collection mise à jour avec succès');

            // Notification de succès
            this.$store.commit("SET_NOTIFICATION_MESSAGE", {
              text: this.$t("collections_and_merch.page.article_selector.notifications.success_update") || "Collection mise à jour avec succès",
              color: "green",
              show: true,
            });

            // Attendre un peu pour montrer la progression, puis rediriger
            setTimeout(() => {
              this.showBuildingCollectionModal = false;
              this.$router.go(-1); // Retour Details qui rechargera depuis API
            }, 1500);

          } catch (error) {
            console.error('❌ Erreur lors du PATCH API:', error);

            // Afficher l'erreur mais rester sur la page Selector
            this.$store.commit("SET_NOTIFICATION_MESSAGE", {
              text: `Erreur lors de l'enregistrement: ${error.message || "Une erreur s'est produite"}`,
              color: "red",
              show: true,
            });

            this.showBuildingCollectionModal = false;
            // NE PAS naviguer en cas d'erreur
            return;
          }
        } else {
          // En création de nouvelle collection - code existant inchangé
          localStorage.setItem("collectionMode", mode);

          if (mode === "auto") {
            localStorage.setItem(
              "collectionFilters",
              JSON.stringify(formattedFilters)
            );
            localStorage.setItem(
              "articleSelectorActiveFilters",
              JSON.stringify(this.activeFilters)
            );
          } else if (mode === "manual") {
            // Sauvegarder les filtres même en mode manuel
            localStorage.setItem(
              "collectionFilters",
              JSON.stringify(formattedFilters)
            );
            localStorage.setItem(
              "articleSelectorActiveFilters",
              JSON.stringify(this.activeFilters)
            );
            // **NOUVEAU** : Marquer spécifiquement les filtres manuels
            localStorage.setItem(
              "manualCollectionFilters",
              JSON.stringify(formattedFilters)
            );
          }

          // Sauvegarder les IDs dans le store Vuex
          this.updateCollectionFormSelectedArticles(articleIds);

          if (mode === "manual") {
            localStorage.setItem(
              "selectedCollectionArticles",
              JSON.stringify(this.persistentSelection)
            );
            // Sauvegarder les données dans le store Vuex
            this.updateCollectionFormSelectedArticlesData(
              this.selectedItemsObjects
            );
          } else {
            localStorage.setItem(
              "selectedCollectionArticles",
              JSON.stringify([])
            );
            // Sauvegarder les données dans le store Vuex
            this.updateCollectionFormSelectedArticlesData(filteredArticles);
          }

          const currentState = JSON.parse(
            localStorage.getItem("collectionCreateState") || "{}"
          );
          localStorage.setItem(
            "collectionCreateState",
            JSON.stringify({
              ...currentState,
              mode: mode,
              articleIds: articleIds,
              filters: formattedFilters,
              sort: this.sortOption,
            })
          );

          // Attendre un peu pour montrer la progression, puis rediriger
          setTimeout(() => {
            this.showBuildingCollectionModal = false;
            this.$router.go(-1);
          }, 1500);
        }
      } catch (error) {
        console.error("Erreur lors de la validation:", error);
        this.$store.commit("SET_NOTIFICATION_MESSAGE", {
          text: `Erreur: ${error.message || "Une erreur s'est produite"}`,
          color: "red",
          show: true,
        });
        // Fermer la modale en cas d'erreur
        this.showBuildingCollectionModal = false;
      } finally {
        this.loading = false;
      }
    },

    toggleFilterSidebar() {
      this.showFilterSidebar = !this.showFilterSidebar;
    },

    // Ajoutez cette méthode pour construire les paramètres de requête avec les bons opérateurs
    buildFilterQueryParams() {
      const filters = {
        locale: this.getClientLocale(),
      };

      // Ajouter le tri dans les filtres (nouveau requirement)
      if (this.sortOption) {
        filters.sort = this.sortOption;
      }

      // Ajouter la recherche si présente
      if (this.searchQuery) {
        filters.search = this.searchQuery;
      }

      // Traiter chaque filtre actif selon le nouveau format
      Object.entries(this.activeFilters).forEach(([key, value]) => {
        // Ignorer les clés techniques
        if (key.startsWith("_")) return;

        // Cas spécial pour le prix - nouveau format attendu par l'API
        if (key === "price" && typeof value === "object") {
          const priceFilter = {};


          if (value.min !== undefined && !isNaN(Number(value.min))) {
            // L'API attend des centimes, et les valeurs arrivent en centimes
            priceFilter.gte = String(Number(value.min));
          }

          if (value.max !== undefined && !isNaN(Number(value.max))) {
            // L'API attend des centimes, et les valeurs arrivent en centimes
            priceFilter.lte = String(Number(value.max));
          }

          if (Object.keys(priceFilter).length > 0) {
            filters.price = priceFilter;
          }
        }
        // Cas spécial pour stock_date - format date avec gte/lte
        else if (key === "stock_date" && typeof value === "object") {
          const stockDateFilter = {};

          if (value.value1) {
            stockDateFilter.gte = value.value1;
          }

          if (value.value2) {
            stockDateFilter.lte = value.value2;
          }

          if (Object.keys(stockDateFilter).length > 0) {
            filters.stock_date = stockDateFilter;
          }
        }
        // Cas pour les tableaux (multi-sélection)
        else if (Array.isArray(value) && value.length > 0) {
          if (value.length === 1) {
            // Une seule valeur = opérateur eq
            filters[key] = { eq: value[0] };
          } else {
            // Plusieurs valeurs = opérateur ct avec |
            filters[key] = { ct: value.join("|") };
          }
        }
        // Cas pour les valeurs simples (string)
        else if (typeof value === "string" && value.trim() !== "") {
          filters[key] = { eq: value };
        }
      });

      return filters;
    },

    // Méthode pour obtenir les opérateurs disponibles pour un filtre
    getOperatorsForFilter(key) {
      // Si nous avons les données de structure des filtres
      if (
        this.availableFilters &&
        this.availableFilters[key] &&
        this.availableFilters[key].operator
      ) {
        return this.availableFilters[key].operator;
      }

      // Sinon, retourner des opérateurs par défaut selon le type de filtre
      if (key === "price") {
        return ["gt", "gte", "lt", "lte"];
      } else {
        return ["eq", "ct"];
      }
    },

    // Point d'entrée principal pour le chargement des articles
    async loadArticles() {
      // La double requête est maintenant gérée dans loadAvailableFilters()
      return this.loadArticlesOriginal();
    },

    // Chargement des articles depuis l'API (version originale)
    async loadArticlesOriginal() {
      // Vérifier si un chargement est déjà en cours
      if (this.isLoadingArticles) {
        return;
      }

      // Construire les paramètres pour cette requête
      const currentParams = this.buildFilterQueryParams();
      // Ajouter les paramètres de pagination pour la déduplication
      currentParams.page = this.paginationData.currentPage;
      currentParams.limit = this.paginationData.itemsPerPage;
      const currentParamsString = JSON.stringify(currentParams);

      // Vérifier si c'est exactement les mêmes paramètres que le dernier appel
      if (this.lastLoadArticlesParams === currentParamsString) {
        return;
      }

      // Marquer le début du chargement
      this.isLoadingArticles = true;
      this.lastLoadArticlesParams = currentParamsString;
      this.loading = true;

      try {
        // Sauvegarder les sélections avant de charger de nouveaux articles
        // Pour une collection manuelle en mode édition, préserver la sélection initiale
        const mode = localStorage.getItem("collectionMode") || "auto";
        const isEditingCollection = this.collectionId && mode === "manual";
        const previousSelection = isEditingCollection
          ? [...this.initialPersistentSelection]
          : [...this.persistentSelection];

        // Utiliser notre méthode buildFilterQueryParams pour obtenir les paramètres formatés
        const params = this.buildFilterQueryParams();

        // Ajouter les paramètres de pagination séparément
        params.page = this.paginationData.currentPage;
        params.limit = this.paginationData.itemsPerPage;

        // Appel unique à l'action pour articles filtrés
        const response = await this.$store.dispatch(
          "merchandising-collections/fetchFilteredArticlesForCollection",
          {
            brandId: this.brandId,
            collectionId: this.collectionId,
            params: params,
          }
        );

        // Traitement des données reçues
        if (response && response.items) {
          // Transformer les données pour les adapter au format attendu par le tableau
          this.articles = response.items.map((article) => {
            const currentLocale = this.getClientLocale();
            const metadata = article.ti_metadata?.[currentLocale] || article.ti_metadata?.fr_FR || {};
            return {
              id: article.ti_id,
              reference: article.ti_reference,
              sku: article.ti_sku || article.ti_reference || "-",
              title: metadata.title || article.ti_slug || "Sans titre",
              type: metadata.type || "-",
              couleur: article.ti_color || metadata.color || "-",
              univers: metadata.gender || "-",
              prix: article.ti_priceResale / 100 || 0,
              image: this.extractImageUrl(article),
              rawData: article,
            };
          });

          // Mise à jour des valeurs de filtres disponibles
          if (response.nextValuesFilter) {
            // Initialiser allAvailableFilters au premier appel (sans filtres actifs)
            if (!this.allAvailableFilters && this.activeFiltersCount === 0) {
              this.allAvailableFilters = response.nextValuesFilter;
              this.hasCompleteFilters = true;
            }

            // Initialiser initialFilterValues si non existant
            if (!this.initialFilterValues) {
              const filtersToUse =
                this.allAvailableFilters || response.nextValuesFilter;
              this.initialFilterValues = filtersToUse;
              localStorage.setItem(
                "initialFilterValues",
                JSON.stringify(this.initialFilterValues)
              );

              // Première initialisation de filterValues depuis initialFilterValues
              this.filterValues = JSON.parse(
                JSON.stringify(this.initialFilterValues)
              );

            } else {
              // Pour garantir que tous les filtres restent visibles
              if (this.allAvailableFilters) {
                // Préserver TOUTE la structure des filtres disponibles
                this.filterValues = JSON.parse(
                  JSON.stringify(this.allAvailableFilters)
                );
                // Mettre à jour les compteurs depuis la réponse filtrée
                this.updateFilterCountsFromFilteredResponse(
                  response.nextValuesFilter
                );
              } else {
                // Fallback: mettre à jour seulement les compteurs et statistiques
                this.updateFilterValuesFromResponse(response.nextValuesFilter);
              }
            }

            // Si nous avons des filtres en attente et les valeurs de filtres,nous pouvons maintenant les appliquer
            if (
              this.pendingFilters &&
              Array.isArray(this.pendingFilters) &&
              this.pendingFilters.length > 0
            ) {
              setTimeout(() => {
                this.applyCollectionFilters(this.pendingFilters);
                this.pendingFilters = null; // Nettoyage
                localStorage.removeItem("editingCollectionFilters");
              }, 100);
            }
          }

          // Mise à jour de la pagination
          const totalItems = response.pagination.totalCount || this.articles.length;
          this.paginationData = {
            currentPage: response.pagination?.current || this.paginationData.currentPage,
            totalPages: Math.ceil(totalItems / this.paginationData.itemsPerPage),
            totalItems: totalItems,
            itemsPerPage: this.paginationData.itemsPerPage,
          };

          // Restaurer la sélection précédente
          this.persistentSelection = previousSelection;

          // Mettre à jour le cache des articles sélectionnés
          this.articles.forEach((article) => {
            if (this.persistentSelection.includes(article.id)) {
              this._selectedItemsCache[article.id] = article;
            }
          });

          // Mettre à jour l'UI avec la sélection
          this.$nextTick(() => {
            if (this.$refs.baseTable) {
              const currentPageIds = this.articles.map((a) => a.id);
              const currentPageSelection = this.persistentSelection.filter(
                (id) => currentPageIds.includes(id)
              );
              this.$refs.baseTable.selectedItems = currentPageSelection;
            }
          });

          // Mettre à jour la plage de prix basée sur les articles
          this.updatePriceRangeFromArticles();
        } else {
          this.articles = [];
          this.paginationData = {
            currentPage: 1,
            totalPages: 1,
            totalItems: 0,
            itemsPerPage: this.paginationData.itemsPerPage,
          };
        }
      } catch (error) {
        console.error("Erreur lors du chargement des articles:", error);
        this.$store.commit("SET_NOTIFICATION_MESSAGE", {
          text: `Erreur lors du chargement des articles: ${
            error.message || "Une erreur est survenue"
          }`,
          color: "red",
          show: true,
        });
        this.articles = [];
      } finally {
        this.loading = false;
        // Libérer le flag de chargement après un court délai pour éviter les appels trop rapprochés
        setTimeout(() => {
          this.isLoadingArticles = false;
        }, 100);
      }
    },

    // Méthode pour mettre à jour les statistiques des filtres tout en préservant la structure
    updateFilterValuesFromResponse(nextValuesFilter) {
      // Copier les initialFilterValues pour éviter les mutations directes
      const updatedFilterValues = JSON.parse(
        JSON.stringify(this.initialFilterValues)
      );

      // Pour chaque filtre dans la réponse, mettre à jour seulement les compteurs/statistiques
      Object.keys(nextValuesFilter).forEach((key) => {
        if (updatedFilterValues[key]) {
          // Si c'est un objet avec des propriétés counts ou statistics, mettre à jour celles-ci
          if (nextValuesFilter[key].counts) {
            updatedFilterValues[key].counts = nextValuesFilter[key].counts;
          }
          if (nextValuesFilter[key].statistics) {
            updatedFilterValues[key].statistics =
              nextValuesFilter[key].statistics;
          }
          // Si c'est une plage (comme le prix), mettre à jour min/max
          if (nextValuesFilter[key].min !== undefined) {
            updatedFilterValues[key].min = nextValuesFilter[key].min;
          }
          if (nextValuesFilter[key].max !== undefined) {
            updatedFilterValues[key].max = nextValuesFilter[key].max;
          }
          // Pour d'autres types de données comme les tableaux simples, conserver la structure originale
        }
      });

      // Mettre à jour filterValues avec la version mise à jour
      this.filterValues = updatedFilterValues;
    },

    // Mettre à jour seulement les compteurs depuis une réponse filtrée
    // tout en préservant la structure complète des filtres disponibles
    updateFilterCountsFromFilteredResponse(filteredResponse) {
      if (!filteredResponse || !this.allAvailableFilters) {
        return;
      }

      // Commencer avec la structure complète
      const updatedFilterValues = JSON.parse(
        JSON.stringify(this.allAvailableFilters)
      );

      // Mettre à jour seulement les compteurs/statistiques depuis la réponse filtrée
      Object.keys(filteredResponse).forEach((key) => {
        if (updatedFilterValues[key] && filteredResponse[key]) {
          // Mettre à jour les counts si présents
          if (
            filteredResponse[key].counts &&
            typeof filteredResponse[key].counts === "object"
          ) {
            // On garde toutes les valeurs de allAvailableFilters mais on met à jour les compteurs
            if (updatedFilterValues[key].counts) {
              // Pour chaque valeur disponible, mettre le compteur à 0 si pas présent dans la réponse filtrée
              Object.keys(updatedFilterValues[key].counts).forEach((value) => {
                updatedFilterValues[key].counts[value] =
                  filteredResponse[key].counts[value] || 0;
              });
            }
          }

          // Mettre à jour les statistiques si présentes
          if (filteredResponse[key].statistics) {
            updatedFilterValues[key].statistics =
              filteredResponse[key].statistics;
          }

          // Mettre à jour les plages (min/max) si présentes
          if (filteredResponse[key].min !== undefined) {
            updatedFilterValues[key].min = filteredResponse[key].min;
          }
          if (filteredResponse[key].max !== undefined) {
            updatedFilterValues[key].max = filteredResponse[key].max;
          }
        }
      });

      // Appliquer les changements
      this.filterValues = updatedFilterValues;
    },

    saveSelectionToLocalStorage() {
      // Déterminer le mode avant de sauvegarder
      // Ne pas changer le mode automatiquement si on édite une collection existante
      const effectiveCollectionId =
        this.collectionId || localStorage.getItem("editingCollectionId");
      const isEditingExistingCollection =
        effectiveCollectionId && localStorage.getItem("editingCollectionId");

      if (!isEditingExistingCollection) {
        const mode = this.persistentSelection.length > 0 ? "manual" : "auto";
        localStorage.setItem("collectionMode", mode);
      }

      // Sauvegarder la sélection courante dans le localStorage pour les collections existantes
      if (effectiveCollectionId) {
        // Stocker les IDs d'articles sélectionnés dans le store au lieu du localStorage
        this.$store.dispatch(
          "merchandising-collections/updateSelectedIdsByCollection",
          {
            brandId: this.brandId,
            collectionId: effectiveCollectionId,
            ids: this.persistentSelection,
          }
        );

        const dataStorageKey = `selectedArticlesData_${this.brandId}_${effectiveCollectionId}`;
        localStorage.setItem(
          dataStorageKey,
          JSON.stringify(this.selectedItemsObjects)
        );
      } else {
        // Pour les nouvelles collections, utiliser le store Vuex
        localStorage.setItem(
          "selectedCollectionArticles",
          JSON.stringify(this.persistentSelection)
        );

        // Sauvegarder dans le store Vuex au lieu du localStorage
        this.updateCollectionFormSelectedArticles(this.persistentSelection);
        this.updateCollectionFormSelectedArticlesData(
          this.selectedItemsObjects
        );
      }

      // Si création de collection, mettre à jour aussi le mode dans la clé générique
      if (!effectiveCollectionId) {
        const currentMode =
          localStorage.getItem("collectionMode") ||
          (this.persistentSelection.length > 0 ? "manual" : "auto");
        const currentState = JSON.parse(
          localStorage.getItem("collectionCreateState") || "{}"
        );
        localStorage.setItem(
          "collectionCreateState",
          JSON.stringify({
            ...currentState,
            mode: currentMode,
          })
        );
      }
    },

    updateSelectedItemsList() {
      // Mettre à jour les IDs pour le tableau de sélection des articles
      // Il s'agit de TOUS les articles sélectionnés, pas seulement ceux de la page courante
      const newSelectedItemsIds = [];

      for (const id of this.persistentSelection) {
        // Chercher d'abord dans la page courante
        const articleOnCurrentPage = this.articles.find(
          (article) => article.id === id
        );

        if (articleOnCurrentPage) {
          // Si trouvé sur la page courante, ajouter à la liste et mettre à jour le cache
          newSelectedItemsIds.push(id);
          this._selectedItemsCache[id] = articleOnCurrentPage;
        }
        // Sinon, vérifier le cache et l'ajouter si présent
        else if (this._selectedItemsCache[id]) {
          newSelectedItemsIds.push(id);
        }
      }

      // Ne mettre à jour selectedItemsIds que si nécessaire pour éviter des rendus inutiles
      if (!this.areArraysEqual(this.selectedItemsIds, newSelectedItemsIds)) {
        this.selectedItemsIds = newSelectedItemsIds;
        this.$store.commit("articleSelector/INCREMENT_TABLE_KEY"); // Forcer le re-rendu
      }
    },

    extractImageUrl(article) {
      return article.ti_photo_pack_url || "https://via.placeholder.com/50x70";
    },




    handleSelectionChange(selection) {
      // Éviter les mises à jour inutiles
      if (this.areArraysEqual(selection, this.currentPageSelection)) {
        return;
      }

      // Créer une copie des sélections actuelles pour éviter les mutations
      const currentPageIds = [...this.articles.map((article) => article.id)];
      const otherPagesSelection = this.persistentSelection.filter(
        (id) => !currentPageIds.includes(id)
      );

      // Créer une nouvelle sélection pour éviter la mutation de l'array d'origine
      const newSelection = [...otherPagesSelection, ...selection];

      // Ne mettre à jour que si nécessaire
      if (!this.areArraysEqual(this.persistentSelection, newSelection)) {
        this.persistentSelection = newSelection;

        // Mise à jour du cache des articles sélectionnés - seulement pour les nouveaux articles
        selection.forEach((id) => {
          const article = this.articles.find((a) => a.id === id);
          if (article && !this._selectedItemsCache[id]) {
            this._selectedItemsCache[id] = article;
          }
        });

        // Mettre à jour le store avec les nouvelles données d'articles sélectionnés
        const selectedArticlesData = newSelection
          .map((id) => {
            // Chercher d'abord dans le cache
            if (this._selectedItemsCache[id]) {
              return this._selectedItemsCache[id];
            }
            // Sinon chercher dans les articles actuels
            const article = this.articles.find((a) => a.id === id);
            if (article) {
              this._selectedItemsCache[id] = article;
              return article;
            }
            // Si pas trouvé, garder les données existantes du store si disponibles
            const existingData =
              this.$store.state.articleSelector.currentState.articlesData;
            const existingArticle = existingData.find((a) => a.id === id);
            if (existingArticle) {
              this._selectedItemsCache[id] = existingArticle;
              return existingArticle;
            }
            console.warn("⚠️ Article non trouvé:", id);
            return null;
          })
          .filter(Boolean);

        
        if (selectedArticlesData.length !== newSelection.length) {
          console.warn("⚠️ Certains articles n'ont pas pu être récupérés:", {
            demandés: newSelection.length,
            trouvés: selectedArticlesData.length,
            manquants: newSelection.filter(
              (id) => !selectedArticlesData.find((a) => a.id === id)
            ),
          });
        }
        this.$store.commit(
          "articleSelector/SET_CURRENT_ARTICLES_DATA",
          selectedArticlesData
        );

        // Mise à jour du mode de remplissage et du mode dans localStorage
        // Ne pas changer le mode automatiquement si on édite une collection existante
        const effectiveCollectionId =
          this.collectionId || localStorage.getItem("editingCollectionId");
        const isEditingExistingCollection =
          effectiveCollectionId && localStorage.getItem("editingCollectionId");

        if (!isEditingExistingCollection) {
          if (this.persistentSelection.length > 0) {
           
            this.collectionFillType = "manual";
            localStorage.setItem("collectionMode", "manual");
          } else if (this.persistentSelection.length === 0) {
            // Si aucun article n'est sélectionné mais qu'on a des filtres
            if (Object.keys(this.activeFilters).length > 0) {
             
              this.collectionFillType = "auto";
              localStorage.setItem("collectionMode", "auto");
            }
          }
        } else {
          // Pour une collection existante, AUSSI changer le mode selon la sélection
          if (this.persistentSelection.length > 0) {
           
            this.collectionFillType = "manual";
            localStorage.setItem("collectionMode", "manual");
          } else {
            // Si aucun article sélectionné, vérifier s'il y a des filtres
            if (Object.keys(this.activeFilters).length > 0) {
             
              this.collectionFillType = "auto";
              localStorage.setItem("collectionMode", "auto");
            } else {
              // Ni articles ni filtres - garder le mode existant
              const currentMode =
                localStorage.getItem("collectionMode") || "auto";
              
              this.collectionFillType =
                currentMode === "auto" ? "auto" : "manual";
            }
          }
        }

        // Sauvegarder dans localStorage de manière optimisée
        this.debouncedSaveToLocalStorage();
      }
    },

    // Méthode pour mettre à jour intelligemment les tags sans recréer ceux qui existent déjà
    updateTagsIntelligently(filters) {
     
      // Générer les nouveaux tags à partir des filtres
      const newTags = this.convertFiltersToTags(filters);
     

      // Pour chaque nouveau tag
      newTags.forEach((newTag) => {
        const existingTagIndex = this.filterTags.findIndex(
          (t) => t.type === newTag.type
        );

        if (existingTagIndex !== -1) {
          // Tag existant - mise à jour des propriétés individuellement
         
          const existingTag = this.filterTags[existingTagIndex];
          this.$set(existingTag, "values", newTag.values);
          this.$set(existingTag, "value", newTag.value);
          this.$set(existingTag, "operator", newTag.operator);
          this.$set(existingTag, "label", newTag.label);
          // IMPORTANT: Mettre à jour aussi rawData pour que le popup affiche les bonnes valeurs
          if (newTag.rawData) {
            this.$set(existingTag, "rawData", newTag.rawData);
          }
         
        } else {
          // Nouveau tag - ajouter à la liste
         
          this.filterTags.push(newTag);
        }
      });

      // Supprimer les tags qui n'existent plus dans les filtres
      for (let i = this.filterTags.length - 1; i >= 0; i--) {
        const existingTag = this.filterTags[i];
        const stillExists = newTags.some(
          (newTag) => newTag.type === existingTag.type
        );

        if (!stillExists) {
         
          this.filterTags.splice(i, 1);
        }
      }
    },

    // Ajoutez cette nouvelle méthode pour convertir les filtres en tags
    convertFiltersToTags(filters) {
      if (this.isUpdatingTags) {
       
        return this.filterTags;
      }

      this.isUpdatingTags = true;

      // NETTOYER d'abord les propriétés techniques pour avoir des IDs stables
      const cleanFilters = { ...filters };
      if (cleanFilters._timestamp !== undefined) delete cleanFilters._timestamp;
      if (cleanFilters._fromSidebar !== undefined)
        delete cleanFilters._fromSidebar;

      
      // Initialiser un tableau pour stocker les tags
      const tags = [];

      // Générer un ID stable basé sur le type et le contenu du filtre
      const generateStableId = (type, content) => {
        const contentStr = JSON.stringify(content);
        // Créer un hash simple basé sur le type et le contenu
        let hash = 0;
        const str = `${type}_${contentStr}`;
        for (let i = 0; i < str.length; i++) {
          const char = str.charCodeAt(i);
          hash = (hash << 5) - hash + char;
          hash = hash & hash; // Convertir en entier 32-bit
        }
        return `tag_${type}_${Math.abs(hash)}`;
      };

      // Parcourir les filtres nettoyés et les convertir en tags
      Object.entries(cleanFilters).forEach(([key, value]) => {
        // Ignorer les propriétés techniques
        if (key.startsWith("_") || !value) return;

        // Cas spécial pour le prix
        if (key === "price" && typeof value === "object") {
          if (value.min !== undefined || value.max !== undefined) {
            // S'assurer que les valeurs sont des nombres pour l'affichage
            const minValue =
              value.min !== undefined ? Number(value.min) / 100 : 0;
            const maxValue =
              value.max !== undefined ? Number(value.max) / 100 : "∞";

            tags.push({
              id: generateStableId("price", value),
              type: "price",
              label: this.$t(
                "collections_and_merch.page.article_selector.filters.prix_label"
              ),
              operator: "range",
              value: `${minValue}€ - ${maxValue}€`,
              values: [],
              rawData: value,
            });
          }
        }
        // Cas spécial pour stock_date
        else if (key === "stock_date" && typeof value === "object") {
          if (value.value1 || value.value2) {
            let dateLabel = "";
            let operator = "Entre";

            if (value.value1 && value.value2) {
              dateLabel = `${this.formatDateForDisplay(value.value1)} - ${this.formatDateForDisplay(value.value2)}`;
            } else if (value.value1) {
              dateLabel = this.formatDateForDisplay(value.value1);
              operator = "supérieur à";
            } else if (value.value2) {
              dateLabel = this.formatDateForDisplay(value.value2);
              operator = "inférieur à";
            }

            tags.push({
              id: generateStableId("stock_date", value),
              type: "stock_date",
              label: this.getFilterLabel("stock_date"),
              operator: operator,
              value: dateLabel,
              values: [],
              rawData: value,
            });
          }
        }
        // Cas pour les filtres de type array (comme les couleurs, les catégories, etc.)
        else if (Array.isArray(value)) {
          tags.push({
            id: generateStableId(key, value),
            type: key,
            label: this.getFilterLabel(key),
            operator: "inclus",
            values: value,
            value: value.length > 1 ? `(${value.length})` : value[0],
            rawData: value,
          });
        }
        // Cas pour les filtres de type objet avec opérateur
        else if (typeof value === "object" && value.operator) {
          // Exemple pour un filtre de type "gender" avec opérateur "inclus" et valeurs ["homme", "femme"]
          if (Array.isArray(value.values)) {
            tags.push({
              id: generateStableId(key, value),
              type: key,
              label: this.getFilterLabel(key),
              operator: value.operator,
              values: value.values,
              value:
                value.values.length > 1
                  ? `(${value.values.length})`
                  : value.values[0],
              rawData: value,
            });
          }
          // Pour les filtres de date ou de plage
          else if (value.value1 || value.value2) {
            let dateLabel = "";
            let operator = value.operator;

            if (value.value1 && value.value2) {
              dateLabel = `${value.value1} - ${value.value2}`;
              operator = "Entre";
            } else if (value.value1) {
              dateLabel = value.value1;
              operator = "supérieur à";
            } else if (value.value2) {
              dateLabel = value.value2;
              operator = "inférieur à";
            }

            tags.push({
              id: generateStableId(key, value),
              type: key,
              label: this.getFilterLabel(key),
              operator: operator,
              value: dateLabel,
              values: [],
              rawData: value,
            });
          }
        }
        // Cas simple pour un filtre de type string
        else if (typeof value === "string") {
          tags.push({
            id: generateStableId(key, value),
            type: key,
            label: this.getFilterLabel(key),
            operator: "eq",
            value: value,
            values: [value],
            rawData: value,
          });
        }
      });

      this.isUpdatingTags = false;
      return tags;
    },

    // Méthode utilitaire pour obtenir le libellé d'un filtre
    getFilterLabel(key) {
      const translationKey = `collections_and_merch.page.article_selector.filters.${key}`;
      const translated = this.$t(translationKey);

      // Si la traduction existe (n'est pas la clé elle-même), on la retourne
      if (translated !== translationKey) {
        return translated;
      }

      // Sinon on retourne la clé comme fallback
      return key;
    },

    // Méthode pour gérer la suppression d'un tag
    handleRemoveTag(tag) {
      // Trouver l'index du tag à supprimer
      const index = this.filterTags.findIndex((t) => t.id === tag.id);
      if (index === -1) return;

      // Créer une copie des filtres actifs
      const updatedFilters = { ...this.activeFilters };

      // Supprimer le filtre correspondant
      if (tag.type === "price") {
        delete updatedFilters.price;
      } else if (tag.type === "stock_date" || tag.type.endsWith("Date")) {
        delete updatedFilters[tag.type];
      } else {
        delete updatedFilters[tag.type];
      }

      // Mettre à jour les filtres et les tags
      this.activeFilters = updatedFilters;
      this.filterTags = this.convertFiltersToTags(updatedFilters);

      // Émettre l'événement de changement de filtres
      this.$emit("filters-change", updatedFilters);

      // Recharger les articles avec les nouveaux filtres
      this.loadArticles();
    },

    handleFiltersChange(filters) {
      // Éviter les mises à jour concurrentes
      if (this.isUpdatingTags) {
        return;
      }

      // Éviter les duplications d'événements
      if (filters._timestamp && this.lastTimestamp === filters._timestamp) {
        return;
      }

      // Stocker le timestamp
      if (filters._timestamp) {
        this.lastTimestamp = filters._timestamp;
      }

      // Éviter les mises à jour inutiles, SAUF si c'est un reset depuis la sidebar
      const isResetFromSidebar = Object.keys(filters).length === 0;
      if (isResetFromSidebar && Object.keys(this.activeFilters).length === 0) {
       
        // Continuer pour vider FiltersCollection même si activeFilters est déjà vide
      } else if (
        Object.keys(filters).length === 0 &&
        Object.keys(this.activeFilters).length === 0
      ) {
        return;
      }

      // Supprimer les propriétés techniques
      const cleanFilters = { ...filters };
      if (cleanFilters._fromSidebar !== undefined)
        delete cleanFilters._fromSidebar;
      if (cleanFilters._timestamp !== undefined) delete cleanFilters._timestamp;

      // Comparer avec l'état actuel
      const currentFiltersString = JSON.stringify(this.activeFilters);
      const newFiltersString = JSON.stringify(cleanFilters);

      if (currentFiltersString === newFiltersString) {
        return;
      }

      // Mettre à jour les filtres actifs
      this.activeFilters = JSON.parse(JSON.stringify(cleanFilters));

      // MISE À JOUR INTELLIGENTE DES TAGS (même logique que handleTagUpdate)
      this.updateTagsIntelligently(this.activeFilters);
     

      // Très important: stocker les filtres actifs pour persistance entre les pages
      localStorage.setItem(
        "articleSelectorActiveFilters",
        JSON.stringify(this.activeFilters)
      );

      // Remettre la page à 1
      this.paginationData.currentPage = 1;

      // Si demandé explicitement, charger les articles
      if (filters._fromSidebar) {
        setTimeout(() => {
          this.loadArticles();
        }, 10);
      }
    },

    // Mise à jour de la méthode applyFilters
    applyFilters() {
      // Vérifier si les filtres ont changé depuis la dernière application
      const currentActiveFiltersString = JSON.stringify(this.activeFilters);

      // Si nous n'avons pas d'état précédent ou si les filtres ont changé
      if (
        !this.lastAppliedFilters ||
        this.lastAppliedFilters !== currentActiveFiltersString
      ) {
        // Stocker l'état actuel pour référence future
        this.lastAppliedFilters = currentActiveFiltersString;

        // Mettre à jour la page à 1 pour commencer une nouvelle recherche
        this.paginationData.currentPage = 1;

        // MISE À JOUR INTELLIGENTE DES TAGS (préservation des tags existants)
        this.updateTagsIntelligently(this.activeFilters);

        // Charger les articles avec les filtres formatés
        this.loadArticles();
      }

      // Fermer la sidebar
      if (this.$refs.filterSidebar) {
        this.$refs.filterSidebar.closeSidebar();
      }
    },

    handleTagUpdate(updatedTag) {
      
      // Copier les filtres actifs pour éviter les mutations directes
      const newFilters = { ...this.activeFilters };

      // Mettre à jour le filtre approprié en fonction du type
      if (updatedTag.type === "price") {
        // Vérification approfondie pour le filtre de prix
        if (updatedTag.rawData) {

          // S'assurer que les valeurs sont des nombres valides
          const minValue =
            updatedTag.rawData.min !== undefined
              ? Number(updatedTag.rawData.min)
              : undefined;

          const maxValue =
            updatedTag.rawData.max !== undefined
              ? Number(updatedTag.rawData.max)
              : undefined;

          newFilters.price = {
            min: minValue,
            max: maxValue,
          };
        } else {
          delete newFilters.price;
        }
      } else if (updatedTag.type.endsWith("Date") || updatedTag.type === "stock_date") {
        newFilters[updatedTag.type] = {
          operator: updatedTag.operator,
          value1: updatedTag.value1,
          value2: updatedTag.value2,
        };
      } else {
        // Gérer les autres types de filtres
        if (updatedTag.values && updatedTag.values.length > 0) {
          newFilters[updatedTag.type] = updatedTag.values;
        } else {
          delete newFilters[updatedTag.type];
        }
      }

      // Mettre à jour les filtres
      this.$set(this, "activeFilters", newFilters);

      // UTILISER LA MÊME LOGIQUE QUE handleFiltersChange pour la cohérence
      this.updateTagsIntelligently(newFilters);
     
      // Recharger les articles
      this.loadArticles();
    },

    handleSort() {

      // Sauvegarder dans localStorage pour persistence
      const currentState = JSON.parse(
        localStorage.getItem("collectionCreateState") || "{}"
      );
      localStorage.setItem(
        "collectionCreateState",
        JSON.stringify({
          ...currentState,
          sort: this.sortOption,
        })
      );

      localStorage.setItem("editedCollectionSort", this.sortOption);

      // Re-trier les articles sélectionnés selon le nouveau tri
      this.sortSelectedArticles();

      // Recharger les articles avec le nouveau tri
      this.loadArticles();
    },

    sortSelectedArticles() {
      const articlesData = this.$store.state.articleSelector.currentState.articlesData;
      const sorted = sortArticles(articlesData, this.sortOption);
      if (sorted !== articlesData) {
        this.$store.commit('articleSelector/SET_CURRENT_ARTICLES_DATA', sorted);
      }
    },

    handleSearch(query) {
      this.searchQuery = query;
      this.paginationData.currentPage = 1;

      // Utiliser directement loadArticles sans debounce si on a déjà un debounce sur l'input
      this.loadArticles();
    },

    handlePageChange(newPage) {
      this.paginationData.currentPage = newPage;
      this.loadArticles();
    },

    handleItemsPerPageChange(newItemsPerPage) {
      this.paginationData.itemsPerPage = newItemsPerPage;
      this.paginationData.currentPage = 1; // Retour à la première page
      this.loadArticles();
    },

    getProductImage(item) {
      return (
        item.image ||
        "data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNTAiIGhlaWdodD0iNzAiIHZpZXdCb3g9IjAgMCA1MCA3MCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPHJlY3Qgd2lkdGg9IjUwIiBoZWlnaHQ9IjcwIiBmaWxsPSIjZjBmMGYwIi8+CjxwYXRoIGQ9Ik0yNSAzNUM4LjQzIDM1IDI1IDUyIDI1IDUyUzQxLjU3IDM1IDI1IDM1WiIgZmlsbD0iIzk5OTk5OSIvPgo8L3N2Zz4K"
      );
    },

    resetFilters() {
      this.activeFilters = {};
      this.filterTags = [];
      this.searchQuery = "";
      this.paginationData.currentPage = 1;
      localStorage.removeItem("articleSelectorActiveFilters");
      this.loadArticles();
    },

    async viewProductDetails(item) {
      // Fonction pour visualiser les détails d'un produit sur le site

      try {
        // Utiliser ti_articleUrl (nouvelle structure) ou articleUrl (legacy)
        const articleUrl = item.ti_articleUrl || item.articleUrl || item.rawData?.ti_articleUrl;
        
        if (articleUrl && articleUrl.trim() !== "") {
          // Ouvrir l'URL dans un nouvel onglet
          window.open(articleUrl, "_blank");
        } else {
          // Article non publié ou URL non disponible
          const tiId = item.rawData?.ti_id || item.ti_id || item.id;
          console.warn("URL non disponible pour l'article ti_id:", tiId);
          
          this.$store.commit("SET_NOTIFICATION_MESSAGE", {
            text: this.$t(
              "collections_and_merch.page.article_selector.notifications.errors.article_not_published"
            ),
            color: "red",
            show: true,
          });
        }
      } catch (error) {
        console.error(
          "Erreur lors de l'ouverture de l'URL de l'article:",
          error
        );
        this.$store.commit("SET_NOTIFICATION_MESSAGE", {
          text: this.$t(
            "collections_and_merch.page.article_selector.notifications.errors.get_url_api"
          ),
          color: "red",
          show: true,
        });
      }
    },

    openAutomaticSelectionDialog() {
      this.$store.commit("SET_MODAL", {
        show: true,
        title: this.$t(
          "collections_and_merch.page.article_selector.automatic_selection.title"
        ),
        content: `
      <div>
        <p>Configurez les critères de sélection automatique d'articles pour votre collection.</p>
        <div class="form-group">
          <label>Nombre maximum d'articles :</label>
          <input type="number" id="max-articles" class="form-control" value="20" min="1" max="100">
        </div>
        <div class="form-group">
          <label>Critères de sélection :</label>
          <select id="criteria" class="form-control">
            <option value="newest">Articles les plus récents</option>
            <option value="bestsellers">Meilleures ventes</option>
            <option value="price-asc">Prix croissant</option>
            <option value="price-desc">Prix décroissant</option>
          </select>
        </div>
        <div class="form-group">
          <label>Catégories :</label>
          <div class="checkbox-group">
            <label><input type="checkbox" checked> Tous les articles</label>
          </div>
        </div>
        <p class="info-text">Cette fonction sélectionnera automatiquement les articles selon les critères choisis.</p>
      </div>
    `,
        actions: [
          {
            text: "Annuler",
            onClick: () => {
              this.$store.commit("SET_MODAL", { show: false });
            },
          },
          {
            text: "Appliquer",
            onClick: () => {
              this.$store.commit("SET_MODAL", { show: false });
              this.$store.commit("SET_NOTIFICATION_MESSAGE", {
                text: this.$t(
                  "collections_and_merch.page.article_selector.notifications.automatic_selection_applied"
                ),
                color: "green",
                show: true,
              });
              // Implémentation de la sélection automatique
              this.applyAutomaticSelection();
            },
            primary: true,
          },
        ],
        size: "medium",
      });
    },

    onFiltersCollectionChange(filters) {
      // Vérifier si les filtres sont différents avant de les mettre à jour
      const currentFiltersString = JSON.stringify(this.activeFilters || {});
      const newFiltersString = JSON.stringify(filters || {});

      if (currentFiltersString === newFiltersString) {
        return;
      }

      // Créer une copie profonde pour éviter les mutations et les problèmes de réactivité
      this.activeFilters = JSON.parse(JSON.stringify(filters));
    },

    emitFiltersChange() {
      // Vérifier si les filtres ont changé avant d'émettre l'événement
      const newFilters = JSON.parse(JSON.stringify(this.activeFilters || {}));

      // Si les filtres sont identiques à ceux déjà émis, ne rien faire
      const currentFiltersString = JSON.stringify(
        this._lastEmittedFilters || {}
      );
      const newFiltersString = JSON.stringify(newFilters);

      if (currentFiltersString === newFiltersString) {
        return;
      }

      // Stocker les derniers filtres émis
      this._lastEmittedFilters = newFilters;

      
      this.$emit("filters-change", newFilters);
    },

    updatePriceRangeFromArticles() {
      // Si nous avons déjà des valeurs de prix depuis l'API (allAvailableFilters.price),
      // ne pas les écraser avec les valeurs calculées depuis les articles
      if (
        this.allAvailableFilters &&
        this.allAvailableFilters.price &&
        this.allAvailableFilters.price.min !== undefined &&
        this.allAvailableFilters.price.max !== undefined
      ) {
        // Utiliser les valeurs API qui sont les vraies valeurs complètes
        if (!this.filterValues.price) {
          this.$set(this.filterValues, "price", {});
        }
        this.$set(this.filterValues.price, "min", this.allAvailableFilters.price.min);
        this.$set(this.filterValues.price, "max", this.allAvailableFilters.price.max);
        return;
      }

      // Sinon, fallback sur le calcul depuis les articles (ancien comportement)
      if (this.articles && this.articles.length > 0) {
        // Extraire tous les prix des articles
        const prices = this.articles.map((article) => article.prix || 0);

        // Trouver le prix maximum seulement (puisque nous fixons le min à 0)
        const maxPrice = Math.max(...prices);

        // Arrondir au-dessus pour le maximum
        const roundedMaxPrice = Math.ceil(maxPrice);

        // CORRECTION: Lors de la première requête (sans filtres actifs),
        // utiliser le vrai maximum des produits disponibles
        const hasActiveFilters =
          Object.keys(this.activeFilters).filter((key) => !key.startsWith("_"))
            .length > 0;

        if (!window.originalMaxPriceValue || !hasActiveFilters) {
          // Première fois OU pas de filtres actifs : utiliser le vrai maximum des produits
          window.originalMaxPriceValue = roundedMaxPrice;
          window.maxPriceValue = roundedMaxPrice;
        } else {
          // Avec des filtres actifs, garder le maximum original pour permettre des filtres plus larges
          window.maxPriceValue = Math.max(
            window.originalMaxPriceValue,
            roundedMaxPrice
          );
        }

        // Mettre à jour le filtre de prix dans filterValues
        if (!this.filterValues.price) {
          this.$set(this.filterValues, "price", {});
        }

        this.$set(this.filterValues.price, "min", 0); // Généralement commence à 0
        this.$set(this.filterValues.price, "max", window.maxPriceValue);
      }
    },

    applyAutomaticSelection() {
      // Récupérer les valeurs du modal
      const maxArticles = document.getElementById("max-articles")?.value || 20;
      const criteria = document.getElementById("criteria")?.value || "newest";

      // Afficher un loader
      this.loading = true;

      // Simuler une requête réseau
      setTimeout(() => {
        // Logique de sélection automatique
        let selectedIds = [];

        // Sélectionner les articles selon les critères
        if (criteria === "newest") {
          // Trier par date (déjà effectué par l'API)
          selectedIds = this.articles.slice(0, maxArticles).map((a) => a.id);
        } else if (criteria === "bestsellers") {
          // Simulation de meilleures ventes (aléatoire dans notre cas)
          const shuffled = [...this.articles].sort(() => 0.5 - Math.random());
          selectedIds = shuffled.slice(0, maxArticles).map((a) => a.id);
        } else if (criteria === "price-asc") {
          // Trier par prix croissant
          const sorted = [...this.articles].sort((a, b) => a.prix - b.prix);
          selectedIds = sorted.slice(0, maxArticles).map((a) => a.id);
        } else if (criteria === "price-desc") {
          // Trier par prix décroissant
          const sorted = [...this.articles].sort((a, b) => b.prix - a.prix);
          selectedIds = sorted.slice(0, maxArticles).map((a) => a.id);
        }

        // Mettre à jour la sélection
        this.persistentSelection = [...selectedIds];

        // Mettre à jour le cache des articles sélectionnés
        selectedIds.forEach((id) => {
          const article = this.articles.find((a) => a.id === id);
          if (article) {
            this._selectedItemsCache[id] = article;
          }
        });

        // Mettre à jour l'état du composant
        this.selectedItemsIds = selectedIds;
        this.collectionFillType = "manual";
        this.$store.commit("articleSelector/INCREMENT_TABLE_KEY");

        // Sauvegarder dans localStorage
        this.saveSelectionToLocalStorage();

        // Mettre à jour la sélection dans le tableau principal
        this.$nextTick(() => {
          if (this.$refs.baseTable) {
            this.$refs.baseTable.selectedItems = this.currentPageSelection;
          }
        });

        // Cacher le loader
        this.loading = false;
      }, 800);
    },

    // Charger les filtres disponibles depuis l'API
    async loadAvailableFilters() {
      // Éviter les appels multiples
      if (this._loadingFiltersInProgress) {
        return;
      }
      this._loadingFiltersInProgress = true;

      try {
        // Appel API pour récupérer tous les filtres (le backend gère le cache)
        const filtersResponse = await this.$store.dispatch(
          "merchandising-collections/fetchArticlesFilters",
          this.brandId
        );

        if (filtersResponse) {
          // L'API retourne la structure complète des filtres
          let filters = filtersResponse.structure || filtersResponse;

          // 🆕 Transformer le filtre price de tableau vers objet
          // L'API retourne des centimes, on les convertit en euros (divisé par 100)
          if (filters.price && Array.isArray(filters.price) && filters.price.length === 2) {
            filters.price = {
              min: Math.floor(filters.price[0] / 100), // Centimes → Euros
              max: Math.ceil(filters.price[1] / 100)   // Centimes → Euros
            };
          }

          this.availableFilters = filters;
          this.allAvailableFilters = filtersResponse.values || filters;
          this.hasCompleteFilters = true;

          // Initialiser filterValues avec tous les filtres
          this.filterValues = JSON.parse(
            JSON.stringify(this.allAvailableFilters)
          );
          this.initialFilterValues = JSON.parse(
            JSON.stringify(this.allAvailableFilters)
          );
          localStorage.setItem(
            "initialFilterValues",
            JSON.stringify(this.initialFilterValues)
          );
        }

        // Charger les filtres de la collection si on est en mode édition
        const editingFilters = localStorage.getItem(
          "editingCollectionFilters"
        );

        if (editingFilters) {
          try {
            const parsedFilters = JSON.parse(editingFilters);

            // Si nous avons des filtres valides, les stocker pour application après chargement des articles
            if (Array.isArray(parsedFilters) && parsedFilters.length > 0) {
              // On stocke les filtres pour les appliquer après le chargement des articles
              this.pendingFilters = parsedFilters;
            }
          } catch (error) {
            console.error(
              "Erreur lors du parsing des filtres de collection:",
              error
            );
          }
        }
      } catch (error) {
        console.error("Erreur lors du chargement des filtres:", error);
        this.isLoadingFilters = false;
      }

      // Logique de chargement des articles après le try-catch
      // Si nous avons des filtres en attente, essayer de charger les filterValues depuis localStorage d'abord
      if (
        this.pendingFilters &&
        Array.isArray(this.pendingFilters) &&
        this.pendingFilters.length > 0
      ) {
       
        // Essayer de charger les filterValues - priorité aux filtres complets
        const filterValuesToUse =
          this.allAvailableFilters || this.initialFilterValues;

        if (filterValuesToUse && Object.keys(filterValuesToUse).length > 0) {

          this.filterValues = JSON.parse(JSON.stringify(filterValuesToUse));

          this.isLoadingFilters = false;
          setTimeout(() => {

            this.applyCollectionFilters(this.pendingFilters);
            this.pendingFilters = null; // Nettoyage
            localStorage.removeItem("editingCollectionFilters");
          }, 100);
          // IMPORTANT: Return ici pour éviter le double appel à loadArticles
          return;
        } else {
          this.isLoadingFilters = false;
          setTimeout(() => {
            this.loadArticles();
          }, 50);
          return;
        }
      }

      // Pas de filtres en attente, charger normalement
      this.isLoadingFilters = false;
      setTimeout(() => {
        this.loadArticles();
      }, 50);
    },

    // Nouvelle méthode pour appliquer les filtres de la collection
    applyCollectionFilters(collectionFilters) {
      if (!Array.isArray(collectionFilters) || collectionFilters.length === 0) {
        return;
      }

      // S'assurer que nous avons des valeurs de filtres disponibles
      if (!this.filterValues || Object.keys(this.filterValues).length === 0) {
        return;
      }

      // Initialiser l'objet des filtres actifs
      const newActiveFilters = {};

      // Pour chaque valeur de filtre dans la collection, déterminer sa catégorie
      collectionFilters.forEach((filterValue) => {
        const category = this.determineFilterCategory(filterValue);
        if (category) {
          // Traitement spécial pour les filtres de prix
          if (category === "price") {
            // Initialiser l'objet price s'il n'existe pas
            if (!newActiveFilters.price) {
              newActiveFilters.price = {};
            }

            // Extraire la valeur du format min-(valeur) ou max-(valeur)
            if (filterValue.startsWith("min-(") && filterValue.endsWith(")")) {
              const minValue = filterValue.substring(5, filterValue.length - 1);
              newActiveFilters.price.min = Number(minValue); // Garder en centimes
              
            } else if (
              filterValue.startsWith("max-(") &&
              filterValue.endsWith(")")
            ) {
              const maxValue = filterValue.substring(5, filterValue.length - 1);
              newActiveFilters.price.max = Number(maxValue); // Garder en centimes
            }
          } else {
            // Traitement normal pour les autres types de filtres
            if (!newActiveFilters[category]) {
              newActiveFilters[category] = [];
            }
            // Vérifier si la valeur n'est pas déjà présente
            if (!newActiveFilters[category].includes(filterValue)) {
              newActiveFilters[category].push(filterValue);
            }
          }
        } else {
          console.warn(
            `Catégorie non déterminée pour le filtre: ${filterValue}`
          );
        }
      });


      // Mettre à jour les filtres actifs
      this.activeFilters = newActiveFilters;
      
      // Convertir les filtres en tags pour l'affichage
      this.filterTags = this.convertFiltersToTags(newActiveFilters);

      // Sauvegarder les filtres actifs
      localStorage.setItem(
        "articleSelectorActiveFilters",
        JSON.stringify(newActiveFilters)
      );

      // Attendre un tick pour s'assurer que les filtres actifs sont bien mis à jour
      this.$nextTick(() => {
        // Charger les articles avec les nouveaux filtres
        this.loadArticles();
      });
    },

    determineFilterCategory(filterValue) {
      // Cas spécial pour les filtres de prix (format: min-(valeur) ou max-(valeur))
      if (
        typeof filterValue === "string" &&
        (filterValue.startsWith("min-(") || filterValue.startsWith("max-("))
      ) {
        return "price";
      }

      // Parcourir toutes les catégories dans this.filterValues
      for (const [category, values] of Object.entries(this.filterValues)) {
        // Si values est un tableau
        if (Array.isArray(values)) {
          // Chercher une correspondance exacte ou insensible à la casse
          const match = values.find(
            (v) =>
              v === filterValue ||
              (typeof v === "string" &&
                typeof filterValue === "string" &&
                v.toLowerCase() === filterValue.toLowerCase())
          );

          if (match) {
            return category;
          }
        }
      }

      // Si aucune correspondance n'est trouvée, essayer de déduire
      // basé sur la structure des filtres disponibles (this.availableFilters)
      for (const [filterKey, filterConfig] of Object.entries(
        this.availableFilters
      )) {
        // Vérifier si ce filtre a des opérateurs compatibles
        if (
          filterConfig &&
          Array.isArray(filterConfig.operator) &&
          (filterConfig.operator.includes("eq") ||
            filterConfig.operator.includes("ct"))
        ) {
          // Cas spéciaux pour certains types de filtres
          if (
            filterKey === "size_filters" &&
            /^(XS|S|M|L|XL|XXL|3XL|\d{2})$/i.test(filterValue)
          ) {
            return filterKey;
          }

          if (
            filterKey === "gender" &&
            /^(Homme|Femme|Unisexe)$/i.test(filterValue)
          ) {
            return filterKey;
          }

          if (filterKey === "color_filters") {
            return filterKey;
          }
        }
      }

      // Si toujours aucune correspondance, renvoyer une catégorie par défaut
      console.warn(
        `Aucune catégorie trouvée pour "${filterValue}", utilisation de la catégorie générique`
      );
      return "unknown_category";
    },

    updateFilterValues(nextValuesFilter) {
      if (nextValuesFilter) {
        this.filterValues = nextValuesFilter;
      }
    },
    ...mapActions(["fetchArticleUrl"]),

    // Actions pour le module merchandising-collections (avec fallback localStorage)
    updateCollectionFormSelectedArticles(selectedIds) {
      // TOUJOURS sauvegarder dans localStorage pour le moment (fallback)
      localStorage.setItem(
        "selectedCollectionArticles",
        JSON.stringify(selectedIds)
      );

      try {
        if (this.$store.state["merchandising-collections"]) {
          this.$store.dispatch(
            "merchandising-collections/updateCollectionFormSelectedArticles",
            selectedIds
          );
        } else {
          console.warn("⚠️ Module merchandising-collections non trouvé");
        }
      } catch (error) {
        console.warn("⚠️ Erreur Vuex:", error.message);
      }
    },
    updateCollectionFormSelectedArticlesData(articlesData) {
      // Migration: Utiliser uniquement le store Vuex avec mode preview
      try {
        if (this.$store.state["merchandising-collections"]) {
          this.$store.dispatch(
            "merchandising-collections/updateCollectionFormSelectedArticlesData",
            {
              articlesData: articlesData,
              isPreviewMode: this.isPreviewMode
            }
          );
        } else {
          console.warn("⚠️ Module merchandising-collections non trouvé");
        }
      } catch (error) {
        console.warn("⚠️ Erreur Vuex:", error.message);
      }
    },
  },
  watch: {
    selectedItemsObjects: {
      handler(newValue) {
        this.cachedSelectedObjects = newValue;
        this.lastSelectionLength = this.persistentSelection.length;
      },
      deep: true,
    },
    activeFilters: {
      deep: true,
      handler(newFilters) {
        // si on n’a plus de filtre et aucune sélection → on repasse en “Automatique” (et on masque le cell)
        if (
          Object.keys(newFilters).length === 0 &&
          this.persistentSelection.length === 0
        ) {
          this.collectionFillType = "auto";
        }
      },
    },
  },
};
</script>

<style scoped>
.article-selector {
  min-height: 100vh;
  padding-bottom: 40px;
}

.header-section {
  margin-bottom: 40px;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.header-section_title {
  display: flex;
  align-items: center;
  gap: 16px;
}

.header-section h1 {
  font-size: 24px;
  font-weight: 500;
  margin-bottom: 8px;
}

.btn-back {
  background: none;
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  padding: 0;
}
.btn-back .icon {
  border-radius: 8px;
  padding: 4px 8px;
  transition: all 0.2s ease;
  background-color: transparent;
}
.btn-back .icon:hover {
  background-color: #00000012;
}

.btn {
  padding: 10px 16px;
  border-radius: 6px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  font-weight: 500;
  transition: all 0.2s ease;
}

.btn-primary {
  background: #000;
  color: white;
  border: none;
}

.btn-primary:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.btn-automatic-selection,
.btn-reset {
  width: 100%;
  justify-content: center;
}

.btn-automatic-selection {
  border: 1px solid #e0e0e0;
}

.btn-automatic-selection:hover {
  background-color: #f5f5f5;
}

.custom-footer {
  display: flex;
  gap: 12px;
  justify-content: center;
  align-items: center;
  flex-direction: column;
  width: 100%;
}

.custom-footer .btn {
  width: 100%;
  align-items: center;
  justify-content: center;
}

.custom-header {
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
}

.custom-header h2 {
  font-weight: 500;
  font-size: 18px;
}

.header-subtitle {
  color: #666;
}

/* Styles pour le panneau des articles sélectionnés */
::v-deep .selected-items-panel {
  margin-bottom: 24px;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  background-color: white;
  overflow: hidden;
}

.panel-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 20px;
  background-color: #f9f9f9;
  cursor: pointer;
  border-bottom: 1px solid #e0e0e0;
}

.panel-title {
  font-weight: 500;
  font-size: 12px;
  color: #333;
}

.panel-icon {
  transition: transform 0.3s ease;
  color: #666;
}

.panel-icon.is-open {
  transform: rotate(180deg);
}

.panel-content {
  overflow: hidden;
  overflow-y: auto;
  height: auto;
  max-height: 300px;
}

/* Animations pour l'apparition du panneau de sélection - Vue 2 */
.slide-fade-enter-active,
.slide-fade-leave-active {
  transition: all 0.4s ease;
}

.slide-fade-enter, /* Changé de enter-from à enter pour Vue 2 */
.slide-fade-leave-to {
  /* leave-to est correct pour Vue 2 aussi */
  transform: translateY(-20px);
  opacity: 0;
}

/* Animation pour le collapse du panneau de contenu - Vue 2 */
.collapse-enter-active,
.collapse-leave-active {
  transition: max-height 0.5s ease, opacity 0.5s ease;
  max-height: 1000px;
  overflow: hidden;
  opacity: 1;
}

.collapse-enter, /* Changé de enter-from à enter pour Vue 2 */
.collapse-leave-to {
  /* leave-to est correct pour Vue 2 aussi */
  max-height: 0;
  opacity: 0;
}

/* Personnalisations pour le BaseTable dans le panneau */
.panel-content ::v-deep .table-container {
  border: none;
  box-shadow: none;
  border-radius: 0;
}

.panel-content ::v-deep .actions-wrapper,
.panel-content ::v-deep .active-filters {
  display: none;
}

.panel-content ::v-deep thead {
  visibility: collapse;
}

.view-button {
  background: none;
  border: none;
  cursor: pointer;
  color: #2196f3;
  text-decoration: underline;
  transition: color 0.2s ease;
}

.view-button:hover {
  color: #3f51b5;
}

/* Assurer que le tableau dans le panneau reste bien aligné */
.panel-content .table-wrapper {
  padding: 0;
  margin: 0;
}

/* Réduire la hauteur des lignes dans le tableau du panneau */
.panel-content .table-row td {
  height: 48px;
}

/* Univers badges */
.univers-badge {
  display: inline-block;
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 500;
}

.product-image {
  width: 40px;
  height: 40px;
  border-radius: 4px;
  overflow: hidden;
  background: #f5f5f5;
}

.product-image img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

/* Responsive */
@media (max-width: 768px) {
  .header-section {
    flex-direction: column;
    align-items: flex-start;
    gap: 16px;
  }

  .header-section button:last-child {
    align-self: flex-end;
  }

  .selected-items-panel {
    overflow-x: auto;
  }
}

/* Styles pour la modale de construction de collection */
.building-collection-modal {
  z-index: 9999;
}

.building-collection-content {
  text-align: center;
  padding: 2rem;
}

.building-animation {
  margin-bottom: 2rem;
  display: flex;
  justify-content: center;
  align-items: center;
  height: 80px;
}

.loading-spinner {
  position: relative;
  width: 60px;
  height: 60px;
}

.spinner-circle {
  position: absolute;
  border: 3px solid transparent;
  border-top: 3px solid #4246de;
  border-radius: 50%;
  animation: spin 1.2s linear infinite;
}

.spinner-circle {
  width: 60px;
  height: 60px;
}

.spinner-circle-2 {
  width: 45px;
  height: 45px;
  top: 7.5px;
  left: 7.5px;
  border-top-color: #f73fde;
  animation-duration: 1s;
  animation-direction: reverse;
}

.spinner-circle-3 {
  width: 30px;
  height: 30px;
  top: 15px;
  left: 15px;
  border-top-color: #19847b;
  animation-duration: 0.8s;
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}

.building-collection-content h3 {
  color: #2c3e50;
  font-size: 1.4rem;
  font-weight: 600;
  margin-bottom: 1rem;
}

.building-subtitle {
  color: #7f8c8d;
  font-size: 1rem;
  line-height: 1.5;
  margin: 0;
}

/* Animation d'apparition de la modale */
.building-collection-modal .modal-content {
  animation: buildingModalFadeIn 0.3s ease-out;
}

@keyframes buildingModalFadeIn {
  from {
    opacity: 0;
    transform: scale(0.9);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
}
</style>
